import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save, X, Edit3, ExternalLink, Radio } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Options must match the backend `_archive_valid_platforms` set.
const PLATFORM_OPTIONS = [
  { value: "soundcloud", label: "SoundCloud" },
  { value: "mixcloud", label: "Mixcloud" },
  { value: "youtube", label: "YouTube" },
  { value: "spotify", label: "Spotify" },
  { value: "other", label: "Other" },
];

const TYPE_OPTIONS = [
  { value: "show", label: "Past Show" },
  { value: "interview", label: "Interview" },
  { value: "show_interview", label: "Show w/ Interview" },
];

const EMPTY_CHANNEL = {
  dj_slug: "",
  dj_name: "",
  soundcloud: "",
  mixcloud: "",
  youtube: "",
  spotify: "",
  other_label: "",
  other_url: "",
};

const EMPTY_ENTRY = {
  dj_slug: "",
  entry_type: "show",
  title: "",
  band_featured: "",
  episode_date: "",
  platform: "soundcloud",
  link: "",
  description: "",
};

// One admin card that manages both:
//   (1) DJ channel URLs (the linktree-style buttons on the public page)
//   (2) Individual episode/interview entries
// Only DJs with a channel row OR a logged entry appear on the public
// Archive page — nothing shows up until Sondra adds it here.
export default function ArchiveManager({ token, onUnauthorized }) {
  const authHeader = useMemo(
    () => ({ Authorization: `Bearer ${token}` }),
    [token],
  );
  const [channels, setChannels] = useState([]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editingChannel, setEditingChannel] = useState(null); // dj_slug or "new"
  const [channelForm, setChannelForm] = useState(EMPTY_CHANNEL);
  const [savingChannel, setSavingChannel] = useState(false);

  const [editingEntry, setEditingEntry] = useState(null); // id or "new"
  const [entryForm, setEntryForm] = useState(EMPTY_ENTRY);
  const [savingEntry, setSavingEntry] = useState(false);

  const load = async () => {
    try {
      const [ch, en] = await Promise.all([
        axios.get(`${API}/admin/archive/channels`, { headers: authHeader }),
        axios.get(`${API}/admin/archive/entries`, { headers: authHeader }),
      ]);
      setChannels(ch.data || []);
      setEntries(en.data || []);
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else toast.error("Failed to load archive");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // === Channel handlers ===================================================
  const startAddChannel = () => {
    setEditingChannel("new");
    setChannelForm(EMPTY_CHANNEL);
  };

  const startEditChannel = (ch) => {
    setEditingChannel(ch.dj_slug);
    setChannelForm({
      dj_slug: ch.dj_slug,
      dj_name: ch.dj_name || "",
      soundcloud: ch.soundcloud || "",
      mixcloud: ch.mixcloud || "",
      youtube: ch.youtube || "",
      spotify: ch.spotify || "",
      other_label: ch.other_label || "",
      other_url: ch.other_url || "",
    });
  };

  const cancelChannelEdit = () => {
    setEditingChannel(null);
    setChannelForm(EMPTY_CHANNEL);
  };

  const saveChannel = async (e) => {
    e.preventDefault();
    if (savingChannel) return;
    const slug = channelForm.dj_slug.trim();
    if (!slug) {
      toast.error("DJ slug is required (e.g. `ej-hays`)");
      return;
    }
    const hasAny = [
      channelForm.soundcloud, channelForm.mixcloud, channelForm.youtube,
      channelForm.spotify, channelForm.other_url,
    ].some((v) => v.trim());
    if (!hasAny) {
      toast.error("At least one platform link is required");
      return;
    }
    setSavingChannel(true);
    try {
      await axios.put(
        `${API}/admin/archive/channels/${encodeURIComponent(slug)}`,
        channelForm,
        { headers: authHeader },
      );
      toast.success("Channel saved 🤘");
      cancelChannelEdit();
      load();
    } catch (err) {
      if (err?.response?.status === 401) {
        onUnauthorized?.();
      } else {
        const msg = err?.response?.data?.detail || "Save failed";
        toast.error(typeof msg === "string" ? msg : "Save failed");
      }
    } finally {
      setSavingChannel(false);
    }
  };

  const deleteChannel = async (ch) => {
    if (!window.confirm(`Remove ${ch.dj_name}'s channel from the Archive?`)) return;
    try {
      await axios.delete(
        `${API}/admin/archive/channels/${encodeURIComponent(ch.dj_slug)}`,
        { headers: authHeader },
      );
      toast.success("Channel removed");
      load();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else toast.error("Delete failed");
    }
  };

  // === Entry handlers =====================================================
  const startAddEntry = () => {
    setEditingEntry("new");
    setEntryForm(EMPTY_ENTRY);
  };

  const startEditEntry = (en) => {
    setEditingEntry(en.id);
    setEntryForm({
      dj_slug: en.dj_slug,
      entry_type: en.entry_type,
      title: en.title,
      band_featured: en.band_featured || "",
      episode_date: en.episode_date || "",
      platform: en.platform,
      link: en.link,
      description: en.description || "",
    });
  };

  const cancelEntryEdit = () => {
    setEditingEntry(null);
    setEntryForm(EMPTY_ENTRY);
  };

  const saveEntry = async (e) => {
    e.preventDefault();
    if (savingEntry) return;
    if (!entryForm.dj_slug.trim() || !entryForm.title.trim() || !entryForm.link.trim()) {
      toast.error("DJ, title, and link are required");
      return;
    }
    setSavingEntry(true);
    try {
      if (editingEntry === "new") {
        await axios.post(
          `${API}/admin/archive/entries`,
          entryForm,
          { headers: authHeader },
        );
        toast.success("Episode added 🤘");
      } else {
        await axios.put(
          `${API}/admin/archive/entries/${editingEntry}`,
          entryForm,
          { headers: authHeader },
        );
        toast.success("Episode updated");
      }
      cancelEntryEdit();
      load();
    } catch (err) {
      if (err?.response?.status === 401) {
        onUnauthorized?.();
      } else {
        const msg = err?.response?.data?.detail || "Save failed";
        toast.error(typeof msg === "string" ? msg : "Save failed");
      }
    } finally {
      setSavingEntry(false);
    }
  };

  const deleteEntry = async (en) => {
    if (!window.confirm(`Delete "${en.title}"?`)) return;
    try {
      await axios.delete(
        `${API}/admin/archive/entries/${en.id}`,
        { headers: authHeader },
      );
      toast.success("Episode deleted");
      load();
    } catch (err) {
      if (err?.response?.status === 401) onUnauthorized?.();
      else toast.error("Delete failed");
    }
  };

  // Convenience: dropdown of known DJ slugs = channels + any DJs referenced
  // by existing entries. Kept as a datalist so the user can also type a
  // brand new slug in.
  const knownSlugs = useMemo(() => {
    const set = new Set();
    channels.forEach((c) => set.add(c.dj_slug));
    entries.forEach((e) => set.add(e.dj_slug));
    return Array.from(set).sort();
  }, [channels, entries]);

  return (
    <section
      className="px-4 md:px-10 pt-6"
      data-testid="archive-manager-card"
    >
      <div className="border border-zinc-800 bg-zinc-950 p-6">
        <div className="flex items-center gap-3 mb-2">
          <Radio className="w-5 h-5 text-red-500" />
          <h2 className="font-display text-2xl tracking-tight text-zinc-50">
            Archive (Shows &amp; Interviews)
          </h2>
        </div>
        <p className="text-xs text-zinc-500 tracking-widest uppercase mb-6">
          //_dj_channels + individual_episodes
        </p>

        {/* ================= CHANNELS ================= */}
        <div className="mb-10">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg tracking-tight text-zinc-100">
              DJ Channels
            </h3>
            <button
              type="button"
              onClick={startAddChannel}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs tracking-widest uppercase border border-red-600 text-red-500 hover:bg-red-600 hover:text-white transition-colors"
              data-testid="archive-add-channel-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add Channel
            </button>
          </div>

          {editingChannel && (
            <form
              onSubmit={saveChannel}
              className="border border-red-600/30 bg-black p-4 mb-4 space-y-3"
              data-testid="archive-channel-form"
            >
              <div className="text-xs tracking-widest text-red-500 uppercase">
                {editingChannel === "new" ? "NEW CHANNEL" : `EDIT ${channelForm.dj_slug}`}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="DJ Slug*"
                  hint="URL slug from /djs/... (e.g., ej-hays)"
                  value={channelForm.dj_slug}
                  onChange={(v) => setChannelForm({ ...channelForm, dj_slug: v })}
                  disabled={editingChannel !== "new"}
                  listId="archive-known-slugs"
                  testId="archive-channel-dj-slug"
                />
                <Input
                  label="DJ Display Name (auto-filled)"
                  hint="Overrides only if slug is not in our roster"
                  value={channelForm.dj_name}
                  onChange={(v) => setChannelForm({ ...channelForm, dj_name: v })}
                  testId="archive-channel-dj-name"
                />
              </div>
              <datalist id="archive-known-slugs">
                {knownSlugs.map((s) => <option key={s} value={s} />)}
              </datalist>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input label="SoundCloud URL" placeholder="https://soundcloud.com/..."
                  value={channelForm.soundcloud}
                  onChange={(v) => setChannelForm({ ...channelForm, soundcloud: v })}
                  testId="archive-channel-soundcloud" />
                <Input label="Mixcloud URL" placeholder="https://www.mixcloud.com/..."
                  value={channelForm.mixcloud}
                  onChange={(v) => setChannelForm({ ...channelForm, mixcloud: v })}
                  testId="archive-channel-mixcloud" />
                <Input label="YouTube URL" placeholder="https://youtube.com/@..."
                  value={channelForm.youtube}
                  onChange={(v) => setChannelForm({ ...channelForm, youtube: v })}
                  testId="archive-channel-youtube" />
                <Input label="Spotify URL" placeholder="https://open.spotify.com/..."
                  value={channelForm.spotify}
                  onChange={(v) => setChannelForm({ ...channelForm, spotify: v })}
                  testId="archive-channel-spotify" />
                <Input label="Other Label" placeholder="e.g., Podbean"
                  value={channelForm.other_label}
                  onChange={(v) => setChannelForm({ ...channelForm, other_label: v })}
                  testId="archive-channel-other-label" />
                <Input label="Other URL" placeholder="https://..."
                  value={channelForm.other_url}
                  onChange={(v) => setChannelForm({ ...channelForm, other_url: v })}
                  testId="archive-channel-other-url" />
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="submit"
                  disabled={savingChannel}
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs tracking-widest uppercase bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white"
                  data-testid="archive-channel-save-btn"
                >
                  <Save className="w-3.5 h-3.5" /> Save
                </button>
                <button
                  type="button"
                  onClick={cancelChannelEdit}
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs tracking-widest uppercase border border-zinc-700 text-zinc-300 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
              </div>
            </form>
          )}

          {loading ? (
            <div className="text-xs text-zinc-500 tracking-widest uppercase">//_loading...</div>
          ) : channels.length === 0 ? (
            <div className="text-xs text-zinc-500 tracking-widest uppercase">
              No channels yet. Click &quot;Add Channel&quot; to plug in a DJ&apos;s SoundCloud, Mixcloud, etc.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-800 border border-zinc-800">
              {channels.map((c) => (
                <li key={c.dj_slug} className="p-3 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base text-zinc-50">{c.dj_name}</div>
                    <div className="text-[10px] tracking-[0.25em] text-zinc-500 uppercase mt-0.5">
                      {c.dj_slug}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2 text-[10px] tracking-widest uppercase">
                      {c.soundcloud && <PlatformPill href={c.soundcloud} label="SoundCloud" />}
                      {c.mixcloud && <PlatformPill href={c.mixcloud} label="Mixcloud" />}
                      {c.youtube && <PlatformPill href={c.youtube} label="YouTube" />}
                      {c.spotify && <PlatformPill href={c.spotify} label="Spotify" />}
                      {c.other_url && <PlatformPill href={c.other_url} label={c.other_label || "Other"} />}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => startEditChannel(c)}
                    className="p-2 text-zinc-400 hover:text-red-500"
                    data-testid={`archive-channel-edit-${c.dj_slug}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteChannel(c)}
                    className="p-2 text-zinc-400 hover:text-red-500"
                    data-testid={`archive-channel-delete-${c.dj_slug}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ================= ENTRIES ================= */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg tracking-tight text-zinc-100">
              Episodes / Interviews
            </h3>
            <button
              type="button"
              onClick={startAddEntry}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-xs tracking-widest uppercase border border-red-600 text-red-500 hover:bg-red-600 hover:text-white transition-colors"
              data-testid="archive-add-entry-btn"
            >
              <Plus className="w-3.5 h-3.5" /> Add Episode
            </button>
          </div>

          {editingEntry && (
            <form
              onSubmit={saveEntry}
              className="border border-red-600/30 bg-black p-4 mb-4 space-y-3"
              data-testid="archive-entry-form"
            >
              <div className="text-xs tracking-widest text-red-500 uppercase">
                {editingEntry === "new" ? "NEW EPISODE" : "EDIT EPISODE"}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Input
                  label="DJ Slug*"
                  hint="Must match a slug from /djs (e.g., ej-hays)"
                  value={entryForm.dj_slug}
                  onChange={(v) => setEntryForm({ ...entryForm, dj_slug: v })}
                  listId="archive-known-slugs"
                  testId="archive-entry-dj-slug"
                />
                <Select
                  label="Type"
                  value={entryForm.entry_type}
                  onChange={(v) => setEntryForm({ ...entryForm, entry_type: v })}
                  options={TYPE_OPTIONS}
                  testId="archive-entry-type"
                />
                <Input
                  label="Title*"
                  placeholder="The Vault — Ep 42"
                  value={entryForm.title}
                  onChange={(v) => setEntryForm({ ...entryForm, title: v })}
                  testId="archive-entry-title"
                />
                <Input
                  label="Band Featured (optional)"
                  placeholder="LIVESAY"
                  value={entryForm.band_featured}
                  onChange={(v) => setEntryForm({ ...entryForm, band_featured: v })}
                  testId="archive-entry-band"
                />
                <Input
                  label="Episode Date"
                  hint="YYYY-MM-DD"
                  type="date"
                  value={entryForm.episode_date}
                  onChange={(v) => setEntryForm({ ...entryForm, episode_date: v })}
                  testId="archive-entry-date"
                />
                <Select
                  label="Platform"
                  value={entryForm.platform}
                  onChange={(v) => setEntryForm({ ...entryForm, platform: v })}
                  options={PLATFORM_OPTIONS}
                  testId="archive-entry-platform"
                />
                <div className="md:col-span-2">
                  <Input
                    label="Link*"
                    placeholder="https://soundcloud.com/... or /..."
                    value={entryForm.link}
                    onChange={(v) => setEntryForm({ ...entryForm, link: v })}
                    testId="archive-entry-link"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-[10px] tracking-widest text-zinc-500 uppercase mb-1">
                    Description (optional)
                  </label>
                  <textarea
                    value={entryForm.description}
                    onChange={(e) => setEntryForm({ ...entryForm, description: e.target.value })}
                    rows={2}
                    className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 outline-none px-3 py-2 text-sm text-zinc-100"
                    data-testid="archive-entry-description"
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <button
                  type="submit"
                  disabled={savingEntry}
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs tracking-widest uppercase bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white"
                  data-testid="archive-entry-save-btn"
                >
                  <Save className="w-3.5 h-3.5" /> Save
                </button>
                <button
                  type="button"
                  onClick={cancelEntryEdit}
                  className="inline-flex items-center gap-2 px-3 py-2 text-xs tracking-widest uppercase border border-zinc-700 text-zinc-300 hover:text-white"
                >
                  <X className="w-3.5 h-3.5" /> Cancel
                </button>
              </div>
            </form>
          )}

          {entries.length === 0 ? (
            <div className="text-xs text-zinc-500 tracking-widest uppercase">
              No episodes logged yet.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-800 border border-zinc-800">
              {entries.map((en) => (
                <li key={en.id} className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-base text-zinc-50 truncate">
                      {en.title}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-[10px] tracking-widest text-zinc-500 uppercase mt-1">
                      <span>{en.dj_name}</span>
                      <span>·</span>
                      <span>{TYPE_OPTIONS.find((o) => o.value === en.entry_type)?.label || en.entry_type}</span>
                      {en.band_featured && (
                        <>
                          <span>·</span>
                          <span className="text-red-500">with {en.band_featured}</span>
                        </>
                      )}
                      {en.episode_date && (
                        <>
                          <span>·</span>
                          <span>{en.episode_date}</span>
                        </>
                      )}
                      <span>·</span>
                      <span>{en.platform}</span>
                    </div>
                  </div>
                  <a
                    href={en.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-2 text-zinc-400 hover:text-red-500"
                    title="Open link"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button
                    type="button"
                    onClick={() => startEditEntry(en)}
                    className="p-2 text-zinc-400 hover:text-red-500"
                    data-testid={`archive-entry-edit-${en.id}`}
                  >
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => deleteEntry(en)}
                    className="p-2 text-zinc-400 hover:text-red-500"
                    data-testid={`archive-entry-delete-${en.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// --- Tiny local form primitives ------------------------------------------
function Input({ label, hint, value, onChange, placeholder, disabled, type = "text", listId, testId }) {
  return (
    <div>
      <label className="block text-[10px] tracking-widest text-zinc-500 uppercase mb-1">
        {label}
      </label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        list={listId}
        data-testid={testId}
        className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 outline-none px-3 py-2 text-sm text-zinc-100 disabled:opacity-60"
      />
      {hint && <div className="text-[10px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

function Select({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-[10px] tracking-widest text-zinc-500 uppercase mb-1">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        className="w-full bg-zinc-950 border border-zinc-800 focus:border-red-600 outline-none px-3 py-2 text-sm text-zinc-100"
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

function PlatformPill({ href, label }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 px-2 py-0.5 border border-zinc-700 text-zinc-300 hover:border-red-500 hover:text-red-500"
    >
      <ExternalLink className="w-3 h-3" />
      {label}
    </a>
  );
}
